import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import SiriWave from 'siriwave';

const VIBE_MESSAGES = [
  "I'm feeling that vibe...",
  'The vibe is strong with this one...',
  'Channeling the source code...',
  'Following the white rabbit...',
  'I know kung fu!!!',
  'I see the code in the Matrix now...',
  "Where we're vibing, we don't need roads...",
  'May the vibe be with you...',
];

const MESSAGE_INTERVAL_MS = 5000;

interface VibeWaveformProps {
  active: boolean;
}

const VibeWaveform = ({ active }: VibeWaveformProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveRef = useRef<SiriWave | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (!containerRef.current || !active) return;

    const container = containerRef.current;
    const wave = new SiriWave({
      container,
      style: 'ios9',
      width: container.offsetWidth,
      height: 150,
      speed: 0.04,
      amplitude: 1.5,
      autostart: true,
      cover: true,
    });

    waveRef.current = wave;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && wave.canvas) {
        const newWidth = entry.contentRect.width;
        wave.canvas.width = newWidth * (wave.opt.ratio ?? window.devicePixelRatio);
        wave.canvas.style.width = `${newWidth}px`;
        wave.width = newWidth;
      }
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      wave.dispose();
      waveRef.current = null;
    };
  }, [active]);

  useEffect(() => {
    if (!active) {
      setMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % VIBE_MESSAGES.length);
    }, MESSAGE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [active]);

  if (!active) return null;

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.default',
        position: 'relative',
        animation: 'vibeWaveformFadeIn 0.5s ease-out',
        '@keyframes vibeWaveformFadeIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: 150,
          '& canvas': {
            display: 'block',
          },
        }}
      />

      <Typography
        key={messageIndex}
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'text.secondary',
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          animation: 'vibeTextCycle 5s ease-in-out',
          '@keyframes vibeTextCycle': {
            '0%': { opacity: 0 },
            '10%': { opacity: 1 },
            '85%': { opacity: 1 },
            '100%': { opacity: 0 },
          },
        }}
      >
        {VIBE_MESSAGES[messageIndex]}
      </Typography>
    </Box>
  );
};

export default VibeWaveform;
